/* eslint-disable no-console */
import { Dict } from 'mixpanel-browser';
import { oneSignalMirrorEvent } from 'common/utils/onesignal';
import { Mixpanel } from '.';
import { MixpanelEventName, UserEventProps, CustomEventProps } from './types';
export type { Dict };
export { MixpanelEventName };

// Guest session tokens are 64 hex characters; account ids are numeric. A guest
// token must never become a Mixpanel distinct id (see trackGuest).
const GUEST_TOKEN_PATTERN = /^[0-9a-f]{64}$/i;
export const isGuestToken = (id: unknown): boolean => typeof id === 'string' && GUEST_TOKEN_PATTERN.test(id);

/**
 * Tracks an event for someone who is not signed in, on Mixpanel's own
 * anonymous $device: id, with is_guest true. Never identifies: under the
 * original ID Merge API only an anonymous id can later be merged into the
 * account, which happens once at sign-up (Mixpanel.linkNewAccount). The guest
 * token rides along as the guest_token super property instead. Any
 * $distinct_id a caller built is dropped so it cannot stand in for the real
 * one. Fire-and-forget; never throws into product code.
 */
export const trackGuest = (eventName: MixpanelEventName, props: Dict = {}): void => {
  try {
    const guestProps: Dict = { ...props, is_guest: true };
    delete guestProps.$distinct_id;
    Mixpanel.track(eventName, guestProps);
    oneSignalMirrorEvent(eventName, guestProps);
  } catch (err) {
    console.log('error', `Mixpanel tracking error: ${err}`);
  }
};

const isAccountId = (id: unknown): boolean => {
  const value = id === null || id === undefined ? '' : String(id);
  return value !== '' && !isGuestToken(value);
};

export const mixpanelUserEvent = ({ mixpanelProps = {}, id, eventName }: UserEventProps) => {
  if (!isAccountId(id)) {
    trackGuest(eventName, mixpanelProps);
    return;
  }
  try {
    Mixpanel.identify(id.toString());
    Mixpanel.linkOneSignal(id.toString());
    Mixpanel.track(eventName, mixpanelProps);
    Mixpanel.people.set(mixpanelProps);
    oneSignalMirrorEvent(eventName, mixpanelProps);
  } catch (err) {
    console.log('error', `Mixpanel tracking error: ${err}`);
  }
};

export const mixpanelCustomEvent = ({ mixpanelProps = {}, id, eventName }: CustomEventProps) => {
  // Shared screens pass "user id or guest token"; only an account id may identify.
  if (!isAccountId(id)) {
    trackGuest(eventName, mixpanelProps);
    return;
  }
  try {
    Mixpanel.identify(id.toString());
    Mixpanel.track(eventName, mixpanelProps);
    oneSignalMirrorEvent(eventName, mixpanelProps);
  } catch (err) {
    console.log('error', `Mixpanel tracking error: ${err}`);
  }
};

// The store is bound here by store.ts at creation time rather than imported:
// importing it (statically or dynamically - import/no-cycle counts both)
// closes the cycle slices -> utils -> eventTriggers -> store -> slices.
type BoundStore = { getState: () => unknown };
let boundStore: BoundStore | null = null;
export const bindStore = (s: BoundStore): void => { boundStore = s; };

/**
 * One-line tracking for the app-wide sweep (869edcw81).
 *
 * Existing call sites hand-build $first_name/$distinct_id/$email props at
 * every call; that boilerplate is why most of the app never got events. This
 * reads the signed-in user from the bound store; when nobody is signed in it
 * falls back to trackGuest if a guest session exists (a guest at the paywall),
 * and otherwise tracks on the device id without is_guest (a public shared-link
 * viewer is not a guest). Fire-and-forget by design - callers never await it,
 * and it can never throw into product code.
 */
export const trackEvent = (eventName: MixpanelEventName, props: Dict = {}): void => {
  try {
    const state = boundStore?.getState() as {
      auth?: { user?: { user?: { id?: number }, id?: number } },
      guestSession?: { token?: string | null },
    } | undefined;
    const id = state?.auth?.user?.user?.id ?? state?.auth?.user?.id;
    if (!id) {
      if (state?.guestSession?.token) {
        trackGuest(eventName, props);
        return;
      }
      const anonProps: Dict = { ...props };
      delete anonProps.$distinct_id;
      Mixpanel.track(eventName, anonProps);
      return;
    }
    mixpanelCustomEvent({ eventName, id: id.toString(), mixpanelProps: props });
  } catch {
    // fire-and-forget: tracking must never throw into product code
  }
};

/**
 * Pre-auth variant for events that happen while nobody is signed in (login
 * failed, registration failed, password reset). Tracks against Mixpanel's
 * device-level anonymous distinct_id, which later stitches to the account via
 * the sign-up alias (Mixpanel.linkNewAccount) or a login identify. Same
 * fire-and-forget contract.
 */
export const trackEventAnon = (eventName: MixpanelEventName, props: Dict = {}): void => {
  try {
    Mixpanel.track(eventName, props);
  } catch {
    // fire-and-forget: tracking must never throw into product code
  }
};
